/**
 * Eisenhower matrix — live session room styling (session-experience-redesign).
 */

import { useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useEffect } from 'react';
import { useLiveSessionProgress } from '../../components/prioritization/session/liveExperience/LiveSessionProgressContext';
import { LiveProgressBar } from '../../components/prioritization/session/liveExperience/LiveSessionChrome';

export type EisenhowerQuadrantKey = 'ui' | 'in' | 'un' | 'nn' | null;

export interface EisenhowerTaskItem {
  id: string;
  title: string;
  description?: string;
  quadrant: EisenhowerQuadrantKey;
  sourceId?: string;
}

const liveQuadrantMeta: Record<Exclude<EisenhowerQuadrantKey, null>, {
  short: string;
  long: string;
  desc: string;
  labelClass: string;
  tagClass: string;
}> = {
  ui: {
    short: 'Do',
    long: 'Urgent + Important',
    desc: 'Handle immediately. These are fires that matter.',
    labelClass: 'text-luminous-coral-700',
    tagClass: 'text-luminous-coral-700/90 border-luminous-coral-700/30 bg-luminous-coral-700/7',
  },
  in: {
    short: 'Schedule',
    long: 'Important, Not Urgent',
    desc: 'Plan and commit to a date. High value, no current fire.',
    labelClass: 'text-electric-mustard-600',
    tagClass: 'text-electric-mustard-600/90 border-electric-mustard-600/30 bg-electric-mustard-600/7',
  },
  un: {
    short: 'Delegate',
    long: 'Urgent, Not Important',
    desc: "Someone else can own this. Don't let it consume focus.",
    labelClass: 'text-[var(--text-sec)]',
    tagClass: 'text-[var(--text-sec)] border-[var(--rule-bright)] bg-[var(--bg-raised)]/[0.03]',
  },
  nn: {
    short: 'Eliminate',
    long: 'Neither',
    desc: 'Close it. Backlog debt removed.',
    labelClass: 'text-[var(--text-ter)]',
    tagClass: 'text-[var(--text-ter)] border-[var(--rule)] bg-transparent',
  },
};

function LiveDraggableChip({ item }: { item: EisenhowerTaskItem }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });
  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`bg-[var(--surface)] border border-[var(--rule-bright)]  px-3 py-2 cursor-grab active:cursor-grabbing transition-colors hover:border-electric-mustard-600/50 hover:bg-electric-mustard-600/10 ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="font-body text-[0.8rem] font-medium text-[var(--text-sec)]">{item.title}</div>
      {item.description && (
        <div className="font-body text-[0.7rem] font-light text-[var(--text-ter)] mt-0.5 line-clamp-2 leading-snug max-w-[220px]">
          {item.description}
        </div>
      )}
    </div>
  );
}

function LiveQuadrantZone({
  quadrantKey,
  items,
}: {
  quadrantKey: Exclude<EisenhowerQuadrantKey, null>;
  items: EisenhowerTaskItem[];
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `quadrant-${quadrantKey}` });
  const meta = liveQuadrantMeta[quadrantKey];
  return (
    <div
      ref={setNodeRef}
      className={`bg-[var(--surface)] border  p-3 min-h-[120px] flex flex-col transition-colors ${
        items.length > 0 ? 'border-[var(--rule-bright)]' : 'border-[var(--rule)]'
      } ${isOver ? 'border-electric-mustard-600/50 bg-electric-mustard-600/[0.03]' : ''}`}
    >
      <div className={`font-heading text-[0.54rem] tracking-[0.14em] uppercase mb-0.5 ${meta.labelClass}`}>
        {meta.short}{' '}
        <span className="font-light opacity-50 normal-case tracking-normal text-[0.85em]">— {meta.long}</span>
      </div>
      <div className="font-body text-[0.68rem] font-light text-[var(--text-ter)] mb-2">{meta.desc}</div>
      <div className="flex flex-wrap gap-2 flex-1 content-start">
        {items.map(item => (
          <LiveDraggableChip key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

function LiveUncategorizedZone({ items }: { items: EisenhowerTaskItem[] }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'quadrant-uncategorized' });
  if (items.length === 0) return null;
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-wrap gap-2 min-h-[60px] mb-3.5 transition-colors ${
        isOver ? ' ring-1 ring-electric-mustard-600/40' : ''
      }`}
    >
      {items.map(item => (
        <LiveDraggableChip key={item.id} item={item} />
      ))}
    </div>
  );
}

export interface EisenhowerLiveViewProps {
  items: EisenhowerTaskItem[];
  activeId: string | null;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

export function EisenhowerLiveView({ items, activeId, onDragStart, onDragEnd }: EisenhowerLiveViewProps) {
  const progressCtx = useLiveSessionProgress();
  const uncategorized = items.filter(i => i.quadrant === null);
  const placed = items.length - uncategorized.length;
  const total = items.length;
  const byQ = (q: Exclude<EisenhowerQuadrantKey, null>) => items.filter(i => i.quadrant === q);
  const activeItem = activeId ? items.find(i => i.id === activeId) : null;

  useEffect(() => {
    progressCtx?.setProgress(placed, total);
  }, [placed, total, progressCtx]);

  return (
    <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5">
      <div className="mb-4">
        <div className="font-display text-[1.15rem] font-light text-[var(--text-pri)] mb-1">
          Place each item in the quadrant that best fits.
        </div>
        <div className="font-body text-[0.78rem] font-light text-[var(--text-ter)]">
          Read the description for context. Drag to a quadrant or drop from the list below.
        </div>
      </div>

      <LiveProgressBar placed={placed} total={total} />

      {uncategorized.length > 0 && (
        <div className="font-heading text-[0.48rem] tracking-[0.1em] uppercase text-[var(--text-dim)] mb-2.5">
          Unplaced items — drag into a quadrant
        </div>
      )}

      <DndContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <LiveUncategorizedZone items={uncategorized} />

        <div className="flex items-center justify-center gap-3 py-1.5 font-heading text-[0.5rem] tracking-[0.14em] uppercase text-[var(--text-dim)]">
          <span>← Not urgent</span>
          <span>Urgency →</span>
        </div>

        <div className="grid grid-cols-2 gap-2 min-h-[280px]">
          <LiveQuadrantZone quadrantKey="ui" items={byQ('ui')} />
          <LiveQuadrantZone quadrantKey="in" items={byQ('in')} />
          <LiveQuadrantZone quadrantKey="un" items={byQ('un')} />
          <LiveQuadrantZone quadrantKey="nn" items={byQ('nn')} />
        </div>

        <DragOverlay>
          {activeItem ? <LiveDraggableChip item={activeItem} /> : null}
        </DragOverlay>
      </DndContext>

      <p className="font-heading text-[0.48rem] tracking-[0.1em] uppercase text-[var(--text-dim)] text-center mt-3">
        Progress saves automatically · Pause and return any time
      </p>
    </div>
  );
}
